import { app, authentication } from '@microsoft/teams-js'; // Teams SDK v2
import { sanitizeLanguageCode } from '@procureai/schemas';
import { i18n as I18N } from 'i18next';
import { jwtDecode } from 'jwt-decode';
import { SigninRedirectArgs, User } from 'oidc-client-ts';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthContextProps, useAuth } from 'react-oidc-context';
import { useNavigate } from 'react-router-dom';

import { useRuntime } from '~/components/RuntimeProvider';
import { RouteId } from '~/routes';
import { userManager } from '~/services/oidc';

export const TEAMS_TOKEN_KEY = 'teamsToken';

const createUserFromTokens = (idToken: string, accessToken: string) => {
  const jwt = jwtDecode(idToken);
  const expiresAt = jwt.exp ? jwt.exp : Math.floor(Date.now() / 1000) + 3600;
  return new User({
    id_token: idToken,
    access_token: accessToken || idToken,
    token_type: 'Bearer',
    scope: 'openid profile email',
    profile: jwt as any, // Casting because the typing of jwtDecode is not strict
    expires_at: expiresAt,
  });
};

const updateLanguage = async (i18n: I18N) => {
  const msTeamsContent = await app.getContext();

  const locale = msTeamsContent.app.locale;
  const languageCode = sanitizeLanguageCode(locale.split('-')[0]);

  if (i18n.language !== languageCode) {
    i18n.changeLanguage(languageCode);
  }
};

export const useRuntimeAwareAuth = (): AuthContextProps => {
  const navigate = useNavigate();
  const { runtime, loading: runtimeLoading } = useRuntime();
  const isTeamsEnabled = runtime?.type === 'MSTeams';
  const isBrowserExtension = runtime?.type === 'browserExtension';
  const auth = useAuth();
  const { i18n } = useTranslation();

  useEffect(() => {
    if (isTeamsEnabled) {
      const storedToken = window.localStorage.getItem(TEAMS_TOKEN_KEY);
      if (storedToken) {
        const { id_token, access_token } = JSON.parse(storedToken);
        const user = createUserFromTokens(id_token, access_token);
        userManager.storeUser(user).catch((err) => console.error('Failed to store user', err));
        window.localStorage.removeItem(TEAMS_TOKEN_KEY);
      }
    }
  }, [isTeamsEnabled]);

  useEffect(() => {
    /* Reload the page after returning from the popup auth flow */
    if (!isTeamsEnabled) {
      return;
    }

    let first = true;
    const handler = () => {
      if (first) {
        first = false; // guard: fire only once
        navigate(0);
      }
    };

    updateLanguage(i18n);

    userManager.events.addUserLoaded(handler);
    return () => userManager.events.removeUserLoaded(handler);
  }, [isTeamsEnabled, navigate, i18n]);

  const signinRedirect = async (args?: SigninRedirectArgs) => {
    if (isTeamsEnabled) {
      try {
        const resultToken = await authentication.authenticate({
          url: `${window.location.origin}${RouteId.loginTeamsStart}`,
          width: 600,
          height: 535,
        });

        const { id_token, access_token } = JSON.parse(resultToken);

        // Create User object from the returned token (assuming it's an ID token or similar)
        const user = createUserFromTokens(id_token, access_token);
        await userManager.storeUser(user);
        // The context (react-oidc) will now treat the user as signed in.

        // Notify the auth context that the user has been loaded (for app to render authenticated state)
        await auth.events.load(user, true);
      } catch (err) {
        console.error('Teams login failed or was cancelled: ', err);
      }
    } else if (isBrowserExtension) {
      await auth.signinPopup();
    } else {
      // Not in Teams – use the standard redirect login
      return auth.signinRedirect(args);
    }
  };

  return {
    ...auth,
    signinRedirect,
    isLoading: auth.isLoading || runtimeLoading,
  };
};
